import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { INT_FLOAT_REGEX } from "../../constants/regex";
import useLabels from "../../hooks/useLabels";
import { Input, Select, Switch } from "../FormComponents";
import { Tooltip, CTA } from "../Generic";
import RangeSlider from "../RangeSlider";
import "./styles/index.scss";
import { addHours, format } from "date-fns";
import { computeDynamicStabilizationEndDate, getElapsedTimeWithClass, resendActivationEmail } from "../../services/common";

const TableCell = ({ getValue, row, column, table }) => {
  const [getLabel] = useLabels();
  const initialValue = getValue();

  const columnMeta = column.columnDef.meta;
  const additional = columnMeta?.additional;
  const tableMeta = table.options.meta;
  const [value, setValue] = useState(initialValue);
  const [substance_id, setSubstanceId] = useState(
    Number(
      row.original.id ||
        row.original.substance_id ||
        row.original.value ||
        row.original.analysis_id
    )
  );
  const errors = tableMeta.getErrors();
  
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    tableMeta?.updateData(row.index, column.id, value);
  };

  const onSelectChange = (e) => {
    setValue(e.target.value);
    tableMeta?.updateData(row.index, column.id, e.target.value);
  };

  const getError = () => {
    if (!errors || !errors?.length === 0 || Object.keys(errors)?.length === 0)
      return;

    return errors?.column?.[row.id]?.value;
  };

  const renderRightValue = () => {
    const isStabilization = [
          "STABILIZATION_STATIC", 
          "STABILIZATION_DYNAMIC",
          "STABILIZATION_RESIN"    // only static and dynamic have work_time
        ].includes(row.original?.type);
    
    if (columnMeta?.type === "expireDate" && row.original?.start_date && isStabilization) {
      const workTime = Number(row.original?.treatments?.[0]?.work_time) || 0;
      const isStabilizationStarted = Boolean(row.original?.start_date) === true;
      const elapsedDate = isStabilizationStarted? addHours(new Date(row.original?.start_date), workTime * 24) : null;
      
      let classNameDate;
      let formattedDate;
      const isResinStabilization = row.original?.type === "STABILIZATION_RESIN";
      const isDynamicStabilization = row.original?.type === "STABILIZATION_DYNAMIC";
      const endDate = isDynamicStabilization ? computeDynamicStabilizationEndDate(row.original) : new Date();
      
      if (isDynamicStabilization) {
        const isExpired = new Date() > endDate;
        const { formatted, className } = getElapsedTimeWithClass(
          isExpired ? endDate : new Date(row.original?.start_date),
          new Date(),
          isStabilizationStarted,
        );
        classNameDate = isExpired ? className : null;
        formattedDate = formatted;
      } else {
        const { formatted, className } = getElapsedTimeWithClass(elapsedDate, new Date(), isStabilizationStarted);
        classNameDate = className;
        formattedDate = formatted;
      }
      
      const expireDate = isResinStabilization ? new Date(row.original?.expire_date) : elapsedDate;

      return <span className="stabilization_date">
        <span>{format(expireDate, columnMeta?.dateFormat)}</span>
        <span className={`elapsed_time ${isResinStabilization ? "" : classNameDate}`}>{formattedDate}</span></span>
    } else return value;
  };

  const getUnitOptions = (substance) => {
    const unit_id = substance?.unit_id || null;
    if (!unit_id) return [];

    const options = [
      {
        value: "3",
        label: getLabel("drugBatchUnit_L"),
      },
      {
        value: "4",
        label: getLabel("drugBatchUnit_Kg"),
      },
      {
        value: "5",
        label: getLabel("drugBatchUnit_Unit"),
      },
    ];

    switch (unit_id) {
      case 1:
      case 2:
      case 4:
      case 6:
      case 7:
        // Kg
        return [options[1]];
      case 3:
        // L
        return [options[0]];
      case 5:
        // unit
        return [options[2]];
    }

    return [];
  };

  const renderWarningOrErrorIcon = (status = null, additionalTitle = "") => {
    if (status === 'WARNING') // WARNING FontAwesome
      return (
        <span className="additional icon">
          <FontAwesomeIcon icon="fa-triangle-exclamation" color="orange" size="lg" title={`${columnMeta?.additionalWarningMessage} ${additionalTitle}`} />
        </span>
      );
    else if (status === 'ERROR') // Unicode "!"
      return (
        <span className="additional icon" title={`${columnMeta?.additionalErrorMessage} ${additionalTitle}`}>
          &#10071; {/* Unicode Heavy Exclamation Mark Symbol */}
        </span>
      );
  }
  const renderAdditional = () => {
    switch (additional) {
      case "examColor":
        if (row.original?.isShowFirstLineData)
          return <span className="additional">{row.original?.color}</span>;
        return null;
      case "unit":
        if (row.original?.unit)
          return <span className="additional">{row.original?.unit}</span>;
        return null;
      case "expired":
        const additionalType = columnMeta?.additionalType;
        if (additionalType === "min_stock") {
          const min_stock = parseFloat(row.original?.min_stock);
          const stocks = parseFloat(row.original?.stocks);
          
          if (!isNaN(stocks) && !isNaN(min_stock)) {

            const additionalTitle = `(${min_stock} ${getUnitOptions({ unit_id: row.original?.unit_id})?.[0]?.label})` || '';

            if (min_stock < stocks && stocks <= 1.4 * min_stock) {
              return renderWarningOrErrorIcon("WARNING", additionalTitle);
            } else if (stocks <= min_stock) {
              return renderWarningOrErrorIcon("ERROR", additionalTitle);
            }
          }
        }
        break;
      case "filters":
        const max_capacity_liters = Number(row.original?.max_capacity_liters) || 0;
        const total_filtered_liters = Number(row.original?.total_filtered_liters) || 0;
        
        if (!isNaN(max_capacity_liters) && !isNaN(total_filtered_liters) && max_capacity_liters > 0) {
          const additionalFilterTitle = `(${max_capacity_liters} -> ${total_filtered_liters})`;
          if (total_filtered_liters < max_capacity_liters && total_filtered_liters >= 0.8 * max_capacity_liters) {
            return renderWarningOrErrorIcon("WARNING", additionalFilterTitle);
          } else if (total_filtered_liters >= max_capacity_liters) {
            return renderWarningOrErrorIcon("ERROR", additionalFilterTitle);
          }
        }
        break;

      default:
        return null;
    }
  };

  const getWineColor = (color) => {
    switch (color) {
      case "WHITE":
        return "rgb(249, 213, 115)";
      case "ROSE":
        return "rgb(199, 79, 89)";
      default:
        return "#64242e";
    }
  };

  const getState = (state) => {
    switch (state) {
      case "GRAPE":
        return "fa-cubes-stacked";
      case "MUST":
        return "fa-cubes-stacked";
      case "WINE":
        return "fa-droplet";
      case "BOTTLED":
        return "fa-wine-bottle";
      default:
        return "fa-wine-bottle";
    }
  };

  const getTankType = (type) => {
    switch (type) {
      case "TANK":
        return "fa-cubes-stacked";
      case "AUTOCLAVE":
        return "fa-cubes-stacked";
      case "CASK":
        return "fa-cubes-stacked";
      case "VAT_CASK":
        return "fa-cubes-stacked";
      case "TONNEAU":
        return "fa-droplet";
      case "BARRIQUE":
        return "fa-wine-bottle";
      default:
        return "#64242e";
    }
  };

  const getIcon = (iconType) => {
    const icon = { icon: iconType, style: {} };
    switch (iconType) {
      case "fa-droplet":
        if (row.original?.color)
          icon.style.color = getWineColor(row.original?.color);
        break;

      case "state":
      case "stateWithColor":
        //   if (row.original?.state) icon.icon = getState(row.original.state);
        //   if (row.original?.state === 'BOTTLED') icon.style.transform = 'rotate(-45deg)';
        //   if (iconType === 'stateWithColor' && row.original?.color) icon.style.color = getWineColor(row.original.color);
        icon.icon = null;
        break;

      // case 'tankType':
      //   if (row.original?.type) icon.icon = getTankType(row.original?.type);
      //   break;
    }

    return icon;
  };

  const renderIcon = () => {
    if (columnMeta?.iconType) {
      const isStabilization = [
          "STABILIZATION_STATIC", 
          "STABILIZATION_DYNAMIC", 
          "STABILIZATION_RESIN",    // only static and dynamic have work_time
          "FILTERING"
        ].includes(row.original?.type);
      if (columnMeta?.type === "expireDate" && row.original?.completion_date === null && isStabilization) {
        const started = Boolean(row.original?.start_date);
        return (<span>
            {started ? (
              <FontAwesomeIcon
                icon={"fa-spinner"}
                spin
                // title={getLabel("operationStatusStarted")}
                style={{ color: "#3498db" }}
                size={"sm"}
              />
            ) : (
              <FontAwesomeIcon
                icon={"fa-clock"}
                // title={getLabel("operationStatusPending")}
                color="grey"
                size={"sm"}
              />
            )}
        </span>)
      } else if (isStabilization) return null;

      const icon = getIcon(columnMeta.iconType);
      if (icon?.icon)
        return (
          <span>
            {" "}
            <FontAwesomeIcon icon={icon.icon} style={icon.style} size="xs" color={columnMeta.iconColor || "grey"} />
          </span>
        );
    }
  };

  const mergeByTankAndBatch = (rows) => {
    const map = new Map();
    const round2 = (n) => Math.round(n * 100) / 100;

    for (const item of rows) {
      const key = item.drug_batch_id;

      if (!map.has(key)) {
        map.set(key, { 
          ...item, 
          quantity: round2(Number(item.quantity) || 0),
        });
      } else {
        const current = map.get(key);
        current.quantity = round2(
          (Number(current.quantity) || 0) + (Number(item.quantity) || 0)
        );
      }

    }

    return Array.from(map.values());
  };

  const getBatchDataSelectedOptions = () => {
    const drugId = row.original.substance_id || row.original.value || row.original.product;
    const selectedOptions = columnMeta?.options2?.[drugId] || [];
    const isEnoughStock = selectedOptions?.[0]?.is_enough_stock || false;
    
    return { selectedOptions, selectedOptions2: mergeByTankAndBatch(selectedOptions), isEnoughStock};
  }

  const getBatchDataOptions = () => {
    const drugId = row.original.substance_id || row.original.value || row.original.product;
    const currentDrug = columnMeta?.options?.find(item => item?.id === drugId) || null;
    if (!currentDrug) return [];
    const options = currentDrug?.batches?.map((batch) => ({
      label: `${batch?.batch_number || ""} (${format(
        new Date(batch?.expire_date || null),
        columnMeta?.dateFormat
      )})`,
      value: batch?.batch_number || "",
      stock: batch?.stock || "",
    }));
    
      // ) || [])
      // ?.filter((item) =>
      //   row.original?.batches
      //     ?.map((batch) => batch?.batch_number)
      //     .includes(item?.value)
      // ) || [];
    // console.log('ops 2', options)
    // options?.unshift({ label: "", value: "" });
    return (
      options?.map((o) => {
        return { ...o, substance_id: substance_id };
      }) || []
    );
  };

  const selectRow = () => {
    setValue((prev) => !prev);
    tableMeta?.selectRow(row.index, !value);
  };

  const getExamStatus = ({ min_range, max_range, results }) => {
    const minValue = min_range !== undefined ? min_range : 0.01; // Default min is 0.01
    const maxValue = max_range !== undefined ? max_range : Infinity; // Default max is Infinity
    return results >= minValue && results <= maxValue ? "is_ok" : "is_warning";
  };

  const modalHandler = (e, modalType = null) => {
    e.preventDefault();
    e.stopPropagation();
    const elementId = row.original.substance_id || row.original.value || row.original.product;
    const id = ["substance_id", "BATCH_SELECTOR"].includes(modalType) ? elementId : substance_id;
    tableMeta?.modalHandler(id, modalType);
    if (columnMeta?.type === "select" && tableMeta?.type ==="winemaking")
      tableMeta.setModifyMode(elementId);
  }

  // const getExpiredDateClassName = () => {
  //   const isStabilization = [
  //         "STABILIZATION_STATIC", 
  //         "STABILIZATION_DYNAMIC", 
  //         "STABILIZATION_RESIN"    // only static and dynamic have work_time
  //       ].includes(row.original?.type);
  //   if (columnMeta?.colorExpired &&
  //     row.original?.expire_date &&
  //     new Date(row.original?.expire_date) <= new Date() && !isStabilization) return "expired";
    
  //   if (isStabilization && columnMeta?.type === "expireDate") {
  //     const workTime = Number(row.original?.treatments?.[0]?.work_time) || 0;
  //     const isStabilizationStarted = Boolean(row.original?.start_date) === true;
  //     const elapsedDate = isStabilizationStarted? addHours(new Date(row.original?.start_date), workTime * 24) : null;
      
  //     const { formatted, className } = getElapsedTimeWithClass(elapsedDate, new Date(), isStabilizationStarted);
  //     return className || "";
  //   }
  // }

  const renderAddDrugBatchButton = (selectedOptions, isEnoughStock, drugBatchesLen, isDisabled = false) => {
    return (<button 
      className={`add_drug_batch ${
          isEnoughStock ? "ok" : "warning"
        } ${isDisabled && "disabled"}`}
      onClick={(e) => modalHandler(e, "BATCH_SELECTOR")} 
      disabled={drugBatchesLen <= 0 || isDisabled}
      name="edit">
        <FontAwesomeIcon icon={selectedOptions?.length > 0 ? "fa-pen" : "fa-plus"} size={"xs"}/>
    </button>
    )
  }

  if (columnMeta?.type === "switch") {
    return (
      <Switch
        leftOption={{
          label: getLabel("listView"),
        }}
        rightOption={{
          label: getLabel("gridView"),
        }}
        // toggleHandler={toggleDisplayMode}
        // status={displayModeGrid}
      />
    );
  } else if (columnMeta?.type === "checkbox") {
    return (
      <input
        type="checkbox"
        checked={value}
        // checked={row.getIsSelected()}
        // onChange={row.getToggleSelectedHandler()}
        onChange={() => selectRow()}
      />
    );
  } else if (columnMeta?.type === "email_validity") {
    return (
      <>
        <span>
          {value
            ? getLabel("userEmailVerified")
            : getLabel("userEmailUnverified")}
        </span>
        {!value && (
          <span style={{ marginLeft: "10px" }}>
            <CTA
            disabled={!row.original?.client_email}
              onClick={(e) => resendActivationEmail(e, row.original?.client_email, getLabel, tableMeta.getAxiosPrivate())}
            >
              {getLabel("userEmailResendInvite")}
            </CTA>
          </span>
        )}
      </>
    );
  } else if (columnMeta?.type === "remove_cookie") {
    return (
      <>
        {value === true && (
          <CTA onClick={columnMeta?.onClickHandler}>
            {getLabel("cookie_settings_mandatory_viewed_cookie_policy_deny")}
          </CTA>
        )}
        {!value && <span>{getLabel("cookie_settings_mandatory_no_deny")}</span>}
      </>
    );
  } else if (columnMeta?.type === "select" && ["treatment", "winemaking"].includes(tableMeta?.type)) {
    // skip OXYGENATION, etc...
    if (columnMeta?.is_winemaking && row.original?.protocol_type !== "ADD") return "";
    
    const { selectedOptions, isEnoughStock } = getBatchDataSelectedOptions();
    const drugBatchesLen = getBatchDataOptions()?.length;
    
    const isDisabled = Number(tableMeta.getTotalVolume()) <= 0;

    return (<div className="add_drug_batch_wrapper">
      {(selectedOptions?.length > 0) && (<div>
        {selectedOptions?.filter((_, i) => i < 2)?.map((option, i) => (
          <p key={`drug_batch_${i}`}>
            {option.label} <span className="warehouse_quantity">{option.quantity?.toFixed?.(3)} {option.warehouse_unit || ""}</span>
          </p>
        ))}
        {selectedOptions?.length > 2 && "..."}
        </div>)
      }
      {drugBatchesLen <= 0 && <p>{getLabel("drugNoBatches")}</p>}
      {drugBatchesLen > 0 && isDisabled &&
        <Tooltip
          variant={"info"}
          html={getLabel("treatmentAssignDrugBatchesNoVolumeTooltip")}
          place="right"
          events={["hover"]}
        >
          {renderAddDrugBatchButton(selectedOptions, isEnoughStock, drugBatchesLen, isDisabled)} 
        </Tooltip>}
      <div className="drug_batch_button_wrapper">
        {drugBatchesLen > 0 && !isDisabled && renderAddDrugBatchButton(selectedOptions, isEnoughStock, drugBatchesLen, isDisabled)}
        {drugBatchesLen > 0 && tableMeta?.type !== "winemaking" && <span
            className={`stock-status ${
              isEnoughStock ? "stock-ok" : "stock-bad"
            }`}
            // title={
            //   isEnoughStock
            //     ? getLabel("valueInsideRange")
            //     : getLabel("valueOutsideRange")
            // }
          ></span>}
        </div>
    </div>)
  } 
  // else if (columnMeta?.type === "select" && tableMeta?.type === "treatment") {
  //   if (getBatchDataOptions()?.length <= 1 || true)
  //     return <div className="add_drug_batch_wrapper"><p>{getLabel("drugNoBatches")}</p>
  //     {tableMeta?.type === "treatment" && <button 
  //       onClick={(e) => modalHandler(e)} 
  //       name="edit">
  //         <FontAwesomeIcon icon="fa-plus" size={"xs"}/>
  //       </button>}
  //     </div>;
  //   else
  //     return (
  //       <Select
  //         name={`drug_batch.${row.id}`}
  //         control={columnMeta?.control}
  //         placeholder={getLabel("drugBatchNumbersPlaceholder")}
  //         // error={errors["drug_batch"]}
  //         // error={errors}
  //         options={getBatchDataOptions()}
  //         isSearchable={true}
  //         isLabel={false}
  //         defaultValue={{
  //           value: "",
  //           label: "",
  //         }}
  //       />
  //     );
  // }
  else if (
    columnMeta?.isCompleted &&
    row.original?.opType === "ANALYSIS" &&
    row.original?.results
  ) {
    // && ([7].includes(Number(row.id)))) {
    return (
      <RangeSlider
        min={row.original?.min_range}
        max={row.original?.max_range}
        current={
          row.original?.results?.find(
            (r) =>
              (r?.tank_id === row.original?.tank_id ||
                (row.original?.info?.isGrapeAnalysis &&
                  r?.batch_id === row.original?.batch_id)) &&
              r?.substance_id === row.original?.substance_id
          )?.value || ""
        }
        unit={row.original?.unit}
      />
    );
  } else if (
    (tableMeta?.editedRows[row.id] && columnMeta?.editable) ||
    (tableMeta?.analysisState === "INSERTING" &&
      Number(row.id) >= tableMeta?.minEditableIndex &&
      Number(row.id) < tableMeta?.maxEditableIndex &&
      columnMeta?.editable)
  ) {
    return columnMeta?.type === "select" ? (
      <select onChange={onSelectChange} value={initialValue || ""}>
        {columnMeta?.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ) : (
      <Input
        onChange={(e) => {
          if (row.original?.opType === "ANALYSIS") {
            const isOnTank = row.original?.tank_id !== null;
            const tank_id = row.original?.tank_id || null;
              const batch_id = row.original?.batch_id || null;

            if (tank_id || batch_id) {
              const value = Number(e.target.value);
              // get the current array of substances for the specific tank_id
              const currentSubstances =
                tableMeta.getValues(`column.tanks`) || [];

              // check if the substance already exists
              const existingSubstanceIndex = currentSubstances?.findIndex(
                (substance) =>
                  substance.substance_id === substance_id &&
                  (isOnTank
                    ? substance.tank_id === tank_id
                    : substance.batch_id === batch_id)
              );

              let updatedSubstances;
              if (existingSubstanceIndex === -1) {
                // create a new entry if it doesn't exist
                const newEntry = {
                  substance_id: Number(substance_id),
                  value: value,
                  tank_id: tank_id,
                  batch_id: batch_id,
                };
                updatedSubstances = [...currentSubstances, newEntry];
              } else {
                // update the existing entry
                updatedSubstances = currentSubstances.map(
                  (substance, index) =>
                    index === existingSubstanceIndex
                      ? { ...substance, value } // update the value
                      : substance // keep the existing substance
                );
              }
              // set the updated array back into the form state
              tableMeta.setValue(`column.tanks`, updatedSubstances);
            }
          } else {
            tableMeta.setValue(
              `column.${substance_id}.old`,
              e.target.defaultValue
            );
            tableMeta.setValue(`column.${substance_id}.new`, e.target.value);
            tableMeta.setValue(`column.${substance_id}.substance_id`, row.original?.value || row.original?.substance_id);  
          }
          
          setValue(
            columnMeta?.noStoreValue
              ? ""
              : `${e.target.value || row.original?.quantity}`
          );
        }}
        onBlur={onBlur}
        name={`column.${row.id}.value`}
        register={columnMeta?.register}
        isLabel={false}
        type={columnMeta?.type || "text"}
        required={getLabel("inputRequiredError")}
        customClassName={`quantity`}
        placeholder={
          row.original?.opType === "ANALYSIS"
            ? getLabel("examResultPlaceholder")
            : getLabel("drugQuantityPlaceholder")
        }
        // defaultValue={row.original?.opType === 'PROTOCOL' ? parseInt(value, 10) : value}
        defaultValue={value}
        error={getError()}
        regExpPattern={{
          value: INT_FLOAT_REGEX,
          message: getLabel("errorIntFloat"),
        }}
        isWarning={["ANALYSIS", "PROTOCOL"].includes(row.original?.opType)}
        validate={{
          value: (value) => {
            const numericValue = parseFloat(value);
            if (numericValue === 0) {
              return getLabel("errorDrugQuantityZero");
            }
            if (numericValue < (row.original?.min_range || 0.01)) {
              return row.original?.opType === "ANALYSIS"
                ? getLabel("errorExamQuantityUnderMin")
                : getLabel("errorDrugQuantityUnderMin");
            }
            return true; // return true if no errors
          },
        }}
        min={{
          value: row.original?.min_range?.toString() || 0.01,
          message:
            row.original?.opType === "ANALYSIS"
              ? getLabel("errorExamQuantityUnderMin")
              : getLabel("errorDrugQuantityUnderMin"),
        }}
        max={{
          value: row.original?.max_range?.toString() || null,
          message:
            row.original?.opType === "ANALYSIS"
              ? getLabel("errorExamQuantityOverMax")
              : getLabel("errorDrugQuantityOverMax"),
        }}
      />
    );
  }

  return column?.id?.startsWith("note") || column?.id === "goal" ? (
    <Tooltip variant={"info"} html={value} place="left" events={["hover"]}>
      <span className={"note"}>{value}</span>
    </Tooltip>
  ) : (
    <span
      className={`${
        column?.id === getLabel("examResult")
          ? `quantity ${getExamStatus(row.original)}`
          : ""
      } 
    ${
      columnMeta?.customClassName === "hide" && !row.original?.isTankFirst
        ? "hide"
        : ""
    }`}
    >
      {renderIcon()}{" "}
      <span
        className={`${
          columnMeta?.customClassName === "hide" && !row.original?.isTankFirst
            ? "hide"
            : columnMeta?.customClassName2 || ""
        }`}
      >
        {renderRightValue()}
      </span>
      {renderAdditional()}
    </span>
  );
};

export default TableCell;
